import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Formik } from 'formik';

import parseQueryString from './../../utils/parseQueryString';
import clearUrlParams from './../../utils/clearUrlParams';
import getErrorTranslation from './../../utils/getErrorTranslation';

import Overlay from './../../Components/Overlay/Overlay';
import StepCounter from './../../Components/StepCounter/StepCounter';
import withActionTracker from './../../Components/PageviewTracker/withActionTracker';

import NewEmailStep from './NewEmailStep/NewEmailStep';
import SearchCriteriaStep from './SearchCriteriaStep/SearchCriteriaStep';
import FileUploadStep from './FileUploadStep/FileUploadStep';
import ActiveEmailStep from './ActiveEmailStep/ActiveEmailStep';
import ConfirmationStep from './ConfirmationStep/ConfirmationStep';

import {
  validateActiveEmail,
  createResetRequest,
  confirmResetRequest,
} from './formActions';

import messages from './messages';
import {
  ACTIVE_EMAIL_STEP,
  SEARCH_CRITERIA_STEP,
  FILE_UPLOAD_STEP,
  CONFIRMATION_STEP,
  NEW_EMAIL_STEP,
} from './constants';

const initialFormValues = {
  firstname: '',
  lastname: '',
  birthdate: '',
  city: '',
  possibleEmail1: '',
  possibleEmail2: '',
  newEmail: '',
  activeEmail: '',
  fileUpload: null,
};

const initialConfirmFormValues = { confirmationCode: '' };

const mapStepToCounterIndex = stepConstant => {
  const stepMap = {
    ACTIVE_EMAIL_STEP: 0,
    SEARCH_CRITERIA_STEP: 0,
    FILE_UPLOAD_STEP: 1,
    NEW_EMAIL_STEP: 2,
    CONFIRMATION_STEP: 3,
  };

  return stepMap[stepConstant];
};

class AccountRecoveryContainer extends Component {
  constructor(props) {
    super(props);

    const entryStep = this.props.location.pathname.includes(
      'forgot-credentials'
    )
      ? SEARCH_CRITERIA_STEP
      : ACTIVE_EMAIL_STEP;

    this.state = {
      currentStep: entryStep,
      stepHistory: [entryStep],
    };

    this.submitHandlers = {
      ACTIVE_EMAIL_STEP: (values, callbacks) => {
        validateActiveEmail(values, callbacks, this.toStep);
      },
      SEARCH_CRITERIA_STEP: (_values, { setSubmitting }) => {
        setSubmitting(false);
        this.toStep(FILE_UPLOAD_STEP);
      },
      FILE_UPLOAD_STEP: ({ fileUpload }, { setErrors, setSubmitting }) => {
        setSubmitting(false);
        if (fileUpload) {
          this.toStep(NEW_EMAIL_STEP);
        } else {
          setErrors({
            fileUpload: <FormattedMessage {...messages.fileUploadMissing} />,
          });
        }
      },
      NEW_EMAIL_STEP: (values, callbacks) =>
        createResetRequest(
          values,
          callbacks,
          this.toStep,
          this.state.stepHistory
        ),
      CONFIRMATION_STEP: (values, callbacks) =>
        confirmResetRequest(values, callbacks, () =>
          this.props.history.replace({ pathname: '/recovery/account/success' })
        ),
    };

    this.renderRecoveryWizard = this.renderRecoveryWizard.bind(this);
    this.toStep = this.toStep.bind(this);
  }

  toStep(nextStep) {
    this.setState(
      {
        currentStep: nextStep,
        stepHistory: [...this.state.stepHistory, nextStep],
      },
      () => window.scrollTo(0, 0)
    );
  }

  renderRecoveryWizard(formikProps) {
    const { isSubmitting, values, setErrors, setFieldValue } = formikProps;
    const { error } = parseQueryString(this.props.location.search);
    const genericError =
      error && getErrorTranslation(messages, `LOGIN_ACCOUNT_RECOVERY_${error}`);

    clearUrlParams();

    const handleFileUploadChange = (uploadedImage, error) => {
      if (error === 'FILE_SIZE_EXCEEDED') {
        setErrors({
          fileUpload: <FormattedMessage {...messages.fileUploadSizeExceeded} />,
        });
        return;
      }

      if (error === 'FILE_TYPE_NOT_ACCEPTED') {
        setErrors({
          fileUpload: (
            <FormattedMessage {...messages.fileUploadTypeNotAccepted} />
          ),
        });
        return;
      }

      setFieldValue('fileUpload', uploadedImage);
      setErrors({ fileUpload: null });
      this.props.track('welcome_login_recovery_account_photo_upload_success');
    };

    const { newEmail } = values;

    const stepComponents = {
      ACTIVE_EMAIL_STEP: (
        <ActiveEmailStep {...formikProps} genericError={genericError} />
      ),
      SEARCH_CRITERIA_STEP: <SearchCriteriaStep {...formikProps} />,
      FILE_UPLOAD_STEP: (
        <FileUploadStep
          {...formikProps}
          handleFileUploadChange={handleFileUploadChange}
        />
      ),
      NEW_EMAIL_STEP: <NewEmailStep {...formikProps} />,
      CONFIRMATION_STEP: (
        <ConfirmationStep
          {...formikProps}
          newEmail={newEmail}
          toStep={this.toStep}
        />
      ),
    };

    return (
      <>
        <StepCounter
          currentStep={mapStepToCounterIndex(this.state.currentStep)}
        />
        <form onSubmit={formikProps.handleSubmit}>
          {stepComponents[this.state.currentStep]}
        </form>
        <Overlay isVisible={isSubmitting} />
      </>
    );
  }

  render() {
    const { currentStep } = this.state;
    return (
      <Formik
        initialValues={
          this.state.currentStep === CONFIRMATION_STEP
            ? initialConfirmFormValues
            : initialFormValues
        }
        onSubmit={this.submitHandlers[currentStep]}
        render={this.renderRecoveryWizard}
      />
    );
  }
}

export default withActionTracker(AccountRecoveryContainer);
